/**
 * Spaces Planning Date Spans
 *
 * Shared local-day span helpers for Spaces planning surfaces.
 *
 * Primary responsibilities:
 * - Normalize saved task/event date values into inclusive day spans
 * - Provide calendar day keys and day counts for timeline/calendar rendering
 *
 * Dependencies:
 * - date-fns
 */

import {
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  isValid,
  parseISO,
  startOfDay
} from "date-fns"

export type PlanningDateSpan = {
  startDate: Date
  endDate: Date
  dayCount: number
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const PLANNING_DATE_KEY_FORMAT = "yyyy-MM-dd"

export function parsePlanningDate(value: string | null | undefined) {
  const trimmed = value?.trim()

  if (!trimmed) {
    return null
  }

  const parsed = DATE_ONLY_PATTERN.test(trimmed)
    ? parseISO(`${trimmed}T00:00:00`)
    : parseISO(trimmed)

  return isValid(parsed) ? parsed : null
}

export function getPlanningDateKey(date: Date) {
  return format(date, PLANNING_DATE_KEY_FORMAT)
}

export function getPlanningDateSpan(
  startValue: string | null | undefined,
  endValue: string | null | undefined
): PlanningDateSpan | null {
  const parsedStart = parsePlanningDate(startValue)
  const parsedEnd = parsePlanningDate(endValue)
  const fallbackDate = parsedStart ?? parsedEnd

  if (!fallbackDate) {
    return null
  }

  const startDate = startOfDay(parsedStart ?? fallbackDate)
  const rawEndDate = startOfDay(parsedEnd ?? fallbackDate)
  const endDate = rawEndDate.getTime() < startDate.getTime() ? startDate : rawEndDate

  return {
    startDate,
    endDate,
    dayCount: differenceInCalendarDays(endDate, startDate) + 1
  }
}

export function getPlanningDateSpanKeys(
  startValue: string | null | undefined,
  endValue: string | null | undefined
) {
  const span = getPlanningDateSpan(startValue, endValue)

  if (!span) {
    return []
  }

  return eachDayOfInterval({
    start: span.startDate,
    end: span.endDate
  }).map(getPlanningDateKey)
}
